/**
 * Icon Component
 * 
 * Renders SVG icons as React components via SVGR.
 * All icons are imported from src/assets/icons/ directory.
 */
import './Icon.css';

// Import all icons using SVGR
// Each SVG file exports { ReactComponent }
import { ReactComponent as LogoSaturn } from '../../assets/icons/logo-plotplanet-saturn.svg';
import { ReactComponent as IconTimeline } from '../../assets/icons/icon-timeline.svg';
import { ReactComponent as IconCharacters } from '../../assets/icons/icon-characters.svg';
import { ReactComponent as IconLocations } from '../../assets/icons/icon-locations.svg';
import { ReactComponent as IconEvents } from '../../assets/icons/icon-events.svg';
import { ReactComponent as IconShots } from '../../assets/icons/icon-shots.svg';
import { ReactComponent as IconImages } from '../../assets/icons/icon-images.svg';
import { ReactComponent as IconPrompts } from '../../assets/icons/icon-prompts.svg';
import { ReactComponent as IconImport } from '../../assets/icons/icon-import.svg';
import { ReactComponent as IconStoryboard } from '../../assets/icons/icon-storyboard.svg';
import { ReactComponent as IconMembers } from '../../assets/icons/icon-members.svg';
import { ReactComponent as IconTesting } from '../../assets/icons/icon-testing.svg';
import { ReactComponent as IconPayments } from '../../assets/icons/icon-payments.svg';
import { ReactComponent as IconDesign } from '../../assets/icons/icon-design.svg';
import { ReactComponent as IconProjects } from '../../assets/icons/icon-projects.svg';
import { ReactComponent as IconSynopsis } from '../../assets/icons/icon-synopsis.svg';
import { ReactComponent as IconLifetime } from '../../assets/icons/icon-lifetime.svg';
import { ReactComponent as IconCheck } from '../../assets/icons/icon-check.svg';
import { ReactComponent as IconStar } from '../../assets/icons/icon-star.svg';
import { ReactComponent as IconCredits } from '../../assets/icons/icon-credits.svg';
import { ReactComponent as IconSecure } from '../../assets/icons/icon-secure.svg';
import { ReactComponent as IconGlobe } from '../../assets/icons/icon-globe.svg';
import { ReactComponent as IconFaq } from '../../assets/icons/icon-faq.svg';
import { ReactComponent as IconVolume } from '../../assets/icons/icon-volume.svg';
import { ReactComponent as IconValue } from '../../assets/icons/icon-value.svg';
import { ReactComponent as IconGuarantee } from '../../assets/icons/icon-guarantee.svg';

// New icons added for emoji replacement
import { ReactComponent as IconPlus } from '../../assets/icons/icon-plus.svg';
import { ReactComponent as IconClose } from '../../assets/icons/icon-close.svg';
import { ReactComponent as IconX } from '../../assets/icons/icon-x.svg';
import { ReactComponent as IconSearch } from '../../assets/icons/icon-search.svg';
import { ReactComponent as IconEdit } from '../../assets/icons/icon-edit.svg';
import { ReactComponent as IconWarning } from '../../assets/icons/icon-warning.svg';
import { ReactComponent as IconEye } from '../../assets/icons/icon-eye.svg';
import { ReactComponent as IconEyeOff } from '../../assets/icons/icon-eye-off.svg';
import { ReactComponent as IconLock } from '../../assets/icons/icon-lock.svg';
import { ReactComponent as IconTrash } from '../../assets/icons/icon-trash.svg';
import { ReactComponent as IconCopy } from '../../assets/icons/icon-copy.svg';
import { ReactComponent as IconSparkle } from '../../assets/icons/icon-sparkle.svg';
import { ReactComponent as IconInfo } from '../../assets/icons/icon-info.svg';
import { ReactComponent as IconSync } from '../../assets/icons/icon-sync.svg';
import { ReactComponent as IconFire } from '../../assets/icons/icon-fire.svg';
import { ReactComponent as IconRocket } from '../../assets/icons/icon-rocket.svg';
import { ReactComponent as IconStats } from '../../assets/icons/icon-stats.svg';
import { ReactComponent as IconDatabase } from '../../assets/icons/icon-database.svg';
import { ReactComponent as IconNodeDiamond } from '../../assets/icons/icon-node-diamond.svg';
import { ReactComponent as IconNodeCircle } from '../../assets/icons/icon-node-circle.svg';
import { ReactComponent as IconArrowRight } from '../../assets/icons/icon-arrow-right.svg';
import { ReactComponent as IconKey } from '../../assets/icons/icon-key.svg';
import { ReactComponent as IconLogout } from '../../assets/icons/icon-logout.svg';
import { ReactComponent as IconPin } from '../../assets/icons/icon-pin.svg';
import { ReactComponent as IconPlay } from '../../assets/icons/icon-play.svg';
import { ReactComponent as IconSquare } from '../../assets/icons/icon-square.svg';
import { ReactComponent as IconSave } from '../../assets/icons/icon-save.svg';
import { ReactComponent as IconChevronUp } from '../../assets/icons/icon-chevron-up.svg';
import { ReactComponent as IconChevronDown } from '../../assets/icons/icon-chevron-down.svg';
import { ReactComponent as IconFeedback } from '../../assets/icons/icon-feedback.svg';

const ICONS = {
  'logo-saturn': LogoSaturn,
  'timeline': IconTimeline,
  'characters': IconCharacters,
  'locations': IconLocations,
  'events': IconEvents,
  'shots': IconShots,
  'images': IconImages,
  'prompts': IconPrompts,
  'import': IconImport,
  'storyboard': IconStoryboard,
  'members': IconMembers,
  'testing': IconTesting,
  'payments': IconPayments,
  'design': IconDesign,
  'projects': IconProjects,
  'synopsis': IconSynopsis,
  'lifetime': IconLifetime,
  'check': IconCheck,
  'star': IconStar,
  'credits': IconCredits,
  'secure': IconSecure,
  'globe': IconGlobe,
  'faq': IconFaq,
  'volume': IconVolume,
  'value': IconValue,
  'guarantee': IconGuarantee,
  // New icons for emoji replacement
  'plus': IconPlus,
  'close': IconClose,
  'x': IconX,
  'search': IconSearch,
  'edit': IconEdit,
  'warning': IconWarning,
  'eye': IconEye,
  'eye-off': IconEyeOff,
  'lock': IconLock,
  'trash': IconTrash,
  'copy': IconCopy,
  'sparkle': IconSparkle,
  'info': IconInfo,
  'sync': IconSync,
  'fire': IconFire,
  'rocket': IconRocket,
  'stats': IconStats,
  'database': IconDatabase,
  'node-diamond': IconNodeDiamond,
  'node-circle': IconNodeCircle,
  'arrow-right': IconArrowRight,
  'key': IconKey,
  'logout': IconLogout,
  'pin': IconPin,
  'play': IconPlay,
  'square': IconSquare,
  'save': IconSave,
  'chevron-up': IconChevronUp,
  'chevron-down': IconChevronDown,
  'feedback': IconFeedback,
  'sparkles': IconSparkle, // alias for sparkle
};

/**
 * Icon Component
 * Renders SVG icons as React components via SVGR
 *
 * @param {string} name - Icon name from ICONS map
 * @param {number} size - Icon size in pixels (default: 24)
 * @param {string} color - Icon color (default: currentColor)
 * @param {string} className - Additional CSS classes
 */
export function Icon({
  name,
  size = 24,
  color = 'currentColor',
  className = '',
  ...props
}) {
  const IconComponent = ICONS[name];

  if (!IconComponent) {
    console.warn(`[Icon] Unknown icon name: "${name}"`);
    return null;
  }

  return (
    <span
      className={`icon icon--${name} ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
      }}
      {...props}
    >
      <IconComponent width={size} height={size} style={{ color }} />
    </span>
  );
}

// Export icon names for reference
export const ICON_NAMES = Object.keys(ICONS);

export default Icon;
